/**
 * Headless per-session identity fetcher for {@link CurrentUserProvider}.
 *
 * Mounted with `key={sessionKey}`, so every sign-in session gets a BRAND-NEW
 * `useAuthMeQuery` observable. This is the SE-5482 fix: after sign-out the
 * app runs `client.clearStore()` while the always-mounted watched query is
 * active, which leaves that observable hung — it retains the previous user's
 * last result and its `refetch()` never settles (verified on web: zero
 * AuthMe requests after a second login, neither then nor catch runs). No
 * amount of nudging revives it reliably, so instead of guarding a poisoned
 * observable (the SE-5090 stamping machinery this replaces), the observable
 * itself is remounted per session: a fresh mount against the cleared cache
 * naturally fetches the new session's identity from the network, and the
 * previous user's data cannot leak because nothing from the previous
 * observable survives.
 * @module features/auth/providers/CurrentUserSessionIdentity
 */

import * as Sentry from "@sentry/react-native";
import { useEffect, useRef } from "react";

import {
  AnalyticsTracker,
  getAnalyticsEnvironment,
} from "@/features/analytics";
import { AuthUserFragment, useAuthMeQuery } from "@/generated/graphql";

/**
 * Maximum number of automatic refetch attempts after `useAuthMeQuery` errors.
 * SE-5123: gives the auth/me query a chance to recover from transient backend
 * congestion (saturated Aurora cold start) without leaving the nav stuck on a
 * default-org / null state.
 */
const MAX_AUTH_ME_RETRY_ATTEMPTS = 3;

/** Backoff cap in milliseconds — never wait longer than this between retries. */
const MAX_AUTH_ME_RETRY_BACKOFF_MS = 30_000;

/**
 * Inputs for {@link resolveDataUser}.
 * @property isSignedIn - Whether an access token is present (query un-skipped)
 * @property effectiveData - This session's query data (session-pure by
 * construction: the component remounts per session, so `data`/`previousData`
 * can only come from this session's own observable)
 * @property loading - Whether the identity query is in flight
 * @property hasError - Whether the identity query errored
 */
interface ResolveDataUserInput {
  readonly isSignedIn: boolean;
  readonly effectiveData: { readonly me?: AuthUserFragment | null } | undefined;
  readonly loading: boolean;
  readonly hasError: boolean;
}

/**
 * Derives the context value from this session's query state.
 *
 * Contract (consumers depend on the tri-state):
 * - `null` — signed out, or the query errored without data (SE-5123 keeps
 *   resolving errors to `null` so the splash gate and default-org nav
 *   handling proceed instead of hanging on "loading").
 * - `undefined` — signed in but this session's identity has not arrived yet.
 * - user object — this session's resolved identity.
 * @param input - Session-pure query state
 * @param input.isSignedIn - Whether an access token is present (query un-skipped)
 * @param input.effectiveData - This session's query data
 * @param input.loading - Whether the identity query is in flight
 * @param input.hasError - Whether the identity query errored
 * @returns The current user, `null`, or `undefined` per the contract above
 */
export function resolveDataUser({
  isSignedIn,
  effectiveData,
  loading,
  hasError,
}: ResolveDataUserInput): AuthUserFragment | null | undefined {
  if (!isSignedIn) {
    return null;
  }
  if (effectiveData?.me !== undefined) {
    return effectiveData.me ?? null;
  }
  if (hasError && !loading) {
    return null;
  }
  return undefined;
}

/** Props for {@link CurrentUserSessionIdentity}. */
export interface CurrentUserSessionIdentityProps {
  /** The session's access token (query skips when absent) */
  readonly accessToken: string | undefined;
  /** The session key this mount serves (echoed back with every report) */
  readonly sessionKey: string | undefined;
  /** Reports this session's identity tri-state to the provider */
  readonly onIdentity: (
    sessionKey: string | undefined,
    user: AuthUserFragment | null | undefined
  ) => void;
}

/**
 * Runs the AuthMe query for exactly one sign-in session and reports the
 * resolved identity tri-state upward. Renders nothing.
 * @param props - Session props
 * @param props.accessToken - The session's access token
 * @param props.sessionKey - The session key this mount serves
 * @param props.onIdentity - Identity report callback
 * @returns null — headless component
 */
export function CurrentUserSessionIdentity({
  accessToken,
  sessionKey,
  onIdentity,
}: CurrentUserSessionIdentityProps) {
  const { data, previousData, loading, error, refetch } = useAuthMeQuery({
    skip: !accessToken,
    // Never coalesce the identity query: a batch resolves only when its
    // slowest member does, and on a cold load AuthMe rode behind nine warm-up
    // queries for ~26s. Everything downstream — flag gates, nav visibility —
    // waits on the current user, so a hard refresh on a gated route timed out
    // and bounced to Home. Unbatched it answers in well under a second
    // (SE-6233).
    context: { batch: false },
    onError: queryError => {
      console.error(
        "CurrentUserProvider: Error fetching user data:",
        queryError
      );
    },
  });

  // SE-5123 retry bookkeeping. Refs (not state) avoid re-triggering the
  // retry effect.
  const retryAttemptsRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use previousData as fallback during refetch to prevent "Hey there" flash.
  // Session-pure by construction: this component is remounted per session
  // (SE-5482), so previousData can never carry another user's identity.
  const dataUser = resolveDataUser({
    isSignedIn: Boolean(accessToken),
    effectiveData: data ?? previousData,
    loading,
    hasError: Boolean(error),
  });

  useEffect(() => {
    onIdentity(sessionKey, dataUser);
  }, [onIdentity, sessionKey, dataUser]);

  // SE-5123: Auto-retry transient useAuthMeQuery failures with capped backoff.
  // The auth/me query saturates against a degraded Aurora cold start; without
  // a retry, the user lands in a default-org / null state and the nav races
  // (Ask Gemini hidden, Squad hidden) for the whole session.
  useEffect(() => {
    if (data?.me?.id) {
      retryAttemptsRef.current = 0;
    }
  }, [data?.me?.id]);

  useEffect(() => {
    if (!error || !accessToken) {
      return undefined;
    }
    if (retryAttemptsRef.current >= MAX_AUTH_ME_RETRY_ATTEMPTS) {
      return undefined;
    }
    const delayMs = Math.min(
      MAX_AUTH_ME_RETRY_BACKOFF_MS,
      1000 * 2 ** retryAttemptsRef.current
    );
    retryTimeoutRef.current = setTimeout(() => {
      retryAttemptsRef.current += 1;
      refetch().catch(() => undefined);
    }, delayMs);
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [error, accessToken, refetch]);

  useEffect(() => {
    if (data?.me?.id) {
      Sentry.setUser({
        email: data.me.email ?? "",
        segment: String(data.me.organizationId ?? ""),
        id: String(data.me.id),
        orgId: String(data.me.organizationId ?? ""),
      });
    } else {
      if (data && !data.me) {
        console.warn(
          "CurrentUserProvider: User data query returned but me is null/undefined"
        );
      }
      if (data?.me && !data.me.id) {
        console.warn(
          "CurrentUserProvider: User me object exists but id is null/undefined"
        );
      }
      Sentry.setUser(null);
    }
  }, [data]);

  // SE-4009: mirror the resolved session identity into PostHog.
  // - Registers the org as super-properties (`org_id` / `org_name` on EVERY
  //   subsequent event) so events are org-attributable without touching the
  //   per-event strict validators.
  // - Associates the "organization" group WITH its display name (groups
  //   previously surfaced only the UUID in PostHog).
  // - Enriches the person profile (`$set`) with email / org / role /
  //   environment.
  // The distinct id stays the phone number — the same id ConfirmCode
  // identifies with at OTP login — so existing persons are enriched rather
  // than split.
  useEffect(() => {
    const me = data?.me;
    if (!me?.id) {
      return;
    }

    const orgId = me.organizationId ?? me.organization?.id ?? null;
    const orgName = me.organization?.name ?? null;

    if (orgId) {
      AnalyticsTracker.setOrganizationContext(orgId, orgName);
      AnalyticsTracker.setGroup(
        "organization",
        orgId,
        orgName === null ? undefined : { name: orgName }
      );
    } else {
      AnalyticsTracker.clearOrganizationContext();
    }

    if (me.phoneNumber) {
      AnalyticsTracker.identifyUser(me.phoneNumber, {
        email: me.email ?? null,
        org_id: orgId,
        org_name: orgName,
        role: me.role?.name ?? null,
        environment: getAnalyticsEnvironment(),
      });
    }
  }, [data]);

  return null;
}

CurrentUserSessionIdentity.displayName = "CurrentUserSessionIdentity";
